import { type AztecAddress, createAztecNodeClient, Fr, type Wallet } from "@aztec/aztec.js";
import { L2Client } from "@turnstile-portal/turnstile.js";
import { type AztecDappWallet, AztecRouterProvider, connectAztec } from "@walletmesh/aztec-rpc-wallet";
import type { JSONRPCTransport } from "@walletmesh/jsonrpc";
import { getAztecTokenContractClassID } from "./lib/addresses";

const {
  VITE_AZTEC_NODE_URL: AZTEC_NODE_URL = "https://sandbox.aztec.walletmesh.com/api/v1/public",
  VITE_AZTEC_WALLET_URL: AZTEC_WALLET_URL = "https://sandbox-example-wallet.aztec.walletmesh.com",
  VITE_AZTEC_CHAIN_ID: AZTEC_CHAIN_ID = "aztec:31337",
} = import.meta.env;

export const node = createAztecNodeClient(AZTEC_NODE_URL);

let aztecWalletPromise: Promise<AztecDappWallet> | null = null;
let aztecWalletInstance: AztecDappWallet | null = null;
let walletWindowRef: Window | null = null;
let walletTransportCleanup: (() => void) | null = null;

async function openWalletWindow(): Promise<{ walletWindow: Window; walletOrigin: string }> {
  console.debug("Opening wallet window", AZTEC_WALLET_URL);
  const walletWindow = window.open(AZTEC_WALLET_URL, "walletMeshWallet", "width=800,height=600");
  if (!walletWindow || walletWindow.closed) {
    throw new Error("Failed to open wallet window");
  }

  // Store reference for cleanup
  walletWindowRef = walletWindow;
  console.debug("💾 Wallet window reference stored");

  console.debug("⏳ Waiting for wallet_ready message...");

  // Wait for the wallet_ready message from the wallet
  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      cleanup();
      reject(new Error("Timeout waiting for wallet_ready message"));
    }, 10000); // 10 second timeout

    const handleMessage = (event: MessageEvent) => {
      // Check if message is from the wallet window
      if (event.source !== walletWindow) {
        return;
      }

      // Check if it's the wallet_ready message
      if (event.data?.type === "wallet_ready") {
        console.debug("✅ Received wallet_ready message");
        cleanup();
        resolve({ walletWindow, walletOrigin: AZTEC_WALLET_URL });
      }
    };

    const cleanup = () => {
      clearTimeout(timeout);
      clearInterval(checkInterval);
      window.removeEventListener("message", handleMessage);
    };

    // Listen for the wallet_ready message
    window.addEventListener("message", handleMessage);

    // Check if window is still open
    const checkInterval = setInterval(() => {
      if (walletWindow.closed) {
        cleanup();
        reject(new Error("Wallet window was closed before ready"));
      }
    }, 500);
  });
}

async function createAztecWallet(): Promise<AztecDappWallet> {
  console.debug("🚪 Opening wallet window...");
  const { walletWindow, walletOrigin } = await openWalletWindow();
  console.debug("✅ Wallet window opened successfully");

  console.debug("🔗 Creating cross-window transport...");
  // Create cross-window transport
  const transport = createDappToWalletTransport(walletWindow, walletOrigin) as JSONRPCTransport & {
    cleanup?: () => void;
  };
  console.debug("✅ Transport created successfully:", !!transport);

  // Store cleanup function
  if (transport.cleanup) {
    walletTransportCleanup = transport.cleanup;
    console.debug("🧹 Transport cleanup function stored");
  } else {
    console.debug("⚠️ No cleanup function found on transport");
  }

  console.debug("🎭 Creating provider with Aztec serialization support...");
  // Create provider with Aztec serialization support
  const provider = new AztecRouterProvider(transport);
  console.debug("✅ Provider created successfully:", !!provider);

  console.debug("🤝 Connecting to Aztec wallet with chain ID:", AZTEC_CHAIN_ID);

  // Add timeout to connectAztec call to prevent infinite hanging
  const connectPromise = connectAztec(provider, AZTEC_CHAIN_ID as `aztec:${string}`);
  const timeoutPromise = new Promise((_, reject) => {
    setTimeout(() => reject(new Error("Wallet connection timeout after 120 seconds")), 1200000);
  });

  try {
    console.debug("⏳ Waiting for connectAztec to complete...");
    const result = (await Promise.race([connectPromise, timeoutPromise])) as { wallet: AztecDappWallet };
    const { wallet } = result;
    console.debug("🎉 Aztec wallet connection successful");

    // Store the instance
    aztecWalletInstance = wallet;
    console.debug("💾 Wallet instance stored in aztecWalletInstance");

    // Cache the wallet address with defensive string conversion
    try {
      const address = wallet.getAddress();
      // Defensive conversion to handle edge cases
      if (address && typeof address.toString === "function") {
        console.log("✨ Aztec wallet connected with address:", address.toString());
      }
    } catch (error) {
      console.error("⚠️ Failed to get wallet address:", error);
    }

    return wallet;
  } catch (error) {
    console.error("💥 connectAztec failed:", error);

    // Additional debugging info
    console.debug("🔍 Debug info:", {
      provider: !!provider,
      transport: !!transport,
      chainId: AZTEC_CHAIN_ID,
      walletWindow: !!walletWindow,
      walletWindowClosed: walletWindow?.closed,
    });

    throw error;
  }
}

export class AztecWalletNotConnectedError extends Error {
  constructor() {
    super("Aztec wallet not connected. Please connect your Aztec wallet first.");
    this.name = "AztecWalletNotConnectedError";
  }
}

export async function getAztecWallet() {
  if (!aztecWalletPromise) {
    throw new AztecWalletNotConnectedError();
  }
  return aztecWalletPromise;
}

export async function connectAztecWallet() {
  console.debug("🌟 connectAztecWallet() called");

  // Force a new connection even if one exists
  console.debug("🧹 Clearing previous wallet instance");
  aztecWalletInstance = null;

  console.debug("🏗️ Creating new wallet connection...");
  aztecWalletPromise = createAztecWallet();

  try {
    console.debug("⏳ Awaiting wallet creation...");
    const wallet = await aztecWalletPromise;
    console.debug("✨ Wallet created successfully:", !!wallet);

    return wallet;
  } catch (error) {
    console.error("💥 Error creating wallet:", error);
    throw error;
  }
}

export function isAztecWalletConnected() {
  const connected = aztecWalletInstance !== null;
  console.debug("isAztecWalletConnected:", connected, "aztecWalletInstance:", aztecWalletInstance);
  return connected;
}

export async function getAztecWalletAddress() {
  if (!aztecWalletInstance) {
    return null;
  }
  try {
    const address = aztecWalletInstance.getAddress();
    // Defensive string conversion
    if (address && typeof address.toString === "function") {
      return address;
    }
    return null;
  } catch {
    return null;
  }
}

export function disconnectAztecWallet() {
  // Cleanup transport
  if (walletTransportCleanup) {
    walletTransportCleanup();
    walletTransportCleanup = null;
  }

  // Close wallet window
  if (walletWindowRef && !walletWindowRef.closed) {
    walletWindowRef.close();
  }
  walletWindowRef = null;

  // Clear instances
  aztecWalletInstance = null;
  aztecWalletPromise = null;
}

export function isAztecWalletWindowOpen() {
  return walletWindowRef !== null && !walletWindowRef.closed;
}

export async function isAztecTokenContract(address: AztecAddress) {
  const contractInstance = await node.getContract(address);
  if (!contractInstance) {
    return false;
  }
  return contractInstance.currentContractClassId.equals(Fr.fromString(getAztecTokenContractClassID()));
}

export function getL2Client(wallet: Wallet): L2Client {
  return new L2Client(node, wallet);
}

export async function getL2ClientAsync(): Promise<L2Client> {
  return getL2Client(await getAztecWallet());
}

// Test helper function to set wallet for testing
export function setTestWallet(wallet: Wallet): void {
  aztecWalletInstance = wallet as AztecDappWallet;
  aztecWalletPromise = Promise.resolve(wallet as AztecDappWallet);
}

export type CrossWindowMessage = {
  type: "walletmesh_message";
  origin: string;
  data: unknown;
  id: string;
};

export function createCrossWindowTransport(
  targetWindow: Window,
  targetOrigin: string,
  messageId: string,
): JSONRPCTransport {
  console.debug("🚀 Creating cross-window transport:", { targetOrigin, messageId });
  const messageHandlers = new Set<(message: unknown) => void>();

  const transport: JSONRPCTransport = {
    send: async (message: unknown) => {
      const wrappedMessage: CrossWindowMessage = {
        type: "walletmesh_message",
        origin: window.location.origin,
        data: message,
        id: messageId,
      };
      console.debug("📤 Sending message to wallet:", wrappedMessage);
      targetWindow.postMessage(wrappedMessage, targetOrigin);
    },
    onMessage: (handler: (message: unknown) => void) => {
      console.debug("📥 Registering message handler for:", messageId);
      messageHandlers.add(handler);
      return () => {
        console.debug("🗑️ Removing message handler for:", messageId);
        messageHandlers.delete(handler);
      };
    },
  };

  const handleMessage = (event: MessageEvent) => {
    // First check: Must be from the wallet window we opened
    if (event.source !== targetWindow) {
      return; // Not from our target window, ignore silently
    }

    // Verify origin matches what we expect from the wallet
    if (targetOrigin !== "*" && event.origin !== targetOrigin) {
      console.debug("❌ Message origin mismatch:", { received: event.origin, expected: targetOrigin });
      return;
    }

    // Check if it's our message type
    if (event.data?.type !== "walletmesh_message") {
      return; // Not our message type, ignore silently
    }

    // Check if it's for our message ID
    if (event.data?.id !== messageId) {
      console.debug("❌ Message ID mismatch:", { received: event.data?.id, expected: messageId });
      return;
    }

    console.debug("📨 Received message from wallet:", event.data.data);
    // Forward to all handlers
    for (const handler of messageHandlers) {
      handler(event.data.data);
    }
  };

  window.addEventListener("message", handleMessage);
  console.debug("👂 Message listener registered for:", messageId);

  // Return cleanup function
  return Object.assign(transport, {
    cleanup: () => {
      window.removeEventListener("message", handleMessage);
      messageHandlers.clear();
    },
  });
}

export function createDappToWalletTransport(walletWindow: Window, walletOrigin: string): JSONRPCTransport {
  return createCrossWindowTransport(walletWindow, walletOrigin, "dapp_to_wallet");
}

export function waitForBlock(blockNumber: number, retryInterval = 2000, maxRetries = 30): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    let retryCount = 0;

    const interval = setInterval(async () => {
      console.debug("Waiting for block", blockNumber);
      const currentBlock = await node.getBlockNumber();
      console.debug("Current block is", currentBlock);

      if (currentBlock >= blockNumber) {
        clearInterval(interval);
        resolve();
        return;
      }

      retryCount++;
      if (retryCount >= maxRetries) {
        clearInterval(interval);
        reject(
          new Error(
            `Timeout waiting for block ${blockNumber}. Current block: ${currentBlock}. Max retries (${maxRetries}) exceeded.`,
          ),
        );
      }
    }, retryInterval);
  });
}

export type BlockProgressCallback = (currentBlock: number, targetBlock: number) => void;

export function waitForBlockWithProgress(
  blockNumber: number,
  onProgress?: BlockProgressCallback,
  retryInterval = 2000,
  maxRetries = 30,
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    let retryCount = 0;

    const interval = setInterval(async () => {
      try {
        const currentBlock = await node.getBlockNumber();
        console.debug(`Waiting for block ${blockNumber}, current: ${currentBlock}`);

        if (onProgress) {
          onProgress(currentBlock, blockNumber);
        }

        if (currentBlock >= blockNumber) {
          clearInterval(interval);
          resolve();
          return;
        }

        retryCount++;
        if (retryCount >= maxRetries) {
          clearInterval(interval);
          reject(
            new Error(
              `Timeout waiting for block ${blockNumber}. Current block: ${currentBlock}. Max retries (${maxRetries}) exceeded.`,
            ),
          );
        }
      } catch (error) {
        clearInterval(interval);
        reject(error);
      }
    }, retryInterval);
  });
}
